import React from "react";
import { toast } from "sonner";
import { Copy, MessageCircle, ExternalLink, Share2, Users, ShoppingBag, Shield, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InviteButton } from "@/components/InvitePartner";
import { buildReferralLink } from "@/lib/referral";

const ROWS = [
  {
    icon: Users, key: "referrals-recruit-row", label: "Recruit partners", desc: "Your public recruiting page — grow your downline",
    accent: "text-stone-700 bg-stone-100",
  },
  {
    icon: ShoppingBag, key: "referrals-storefront-row", label: "Sell to clients", desc: "Your product storefront with free AI policy checkup",
    accent: "text-amber-800 bg-amber-100",
  },
  {
    icon: Shield, key: "referrals-dca-row", label: "Sell DCA cover", desc: "Device Care Assurance — instant phone protection quotes",
    accent: "text-emerald-800 bg-emerald-100",
  },
];

const Row = ({ row, url, copyMsg, waMsg, delay }) => {
  const Icon = row.icon;
  const copy = () => { navigator.clipboard.writeText(copyMsg); toast.success("Message + link copied — paste anywhere!"); };
  return (
    <div data-testid={row.key} style={{ animationDelay: `${delay}ms` }}
      className="group bg-white border border-stone-200 rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-emerald-900/30 animate-in fade-in slide-in-from-bottom-2">
      <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${row.accent}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-[180px]">
        <div className="font-display text-base font-black">{row.label}</div>
        <div className="text-xs text-stone-400">{row.desc}</div>
        <code className="text-[11px] font-mono text-emerald-900/70 block truncate mt-0.5">{url}</code>
      </div>
      <div className="flex gap-2 ml-auto">
        <button onClick={copy} data-testid={`${row.key}-copy`} title="Copy message + link"
          className="w-10 h-10 grid place-items-center rounded-full bg-emerald-900 text-white transition-all duration-300 ease-out hover:bg-emerald-950 hover:-translate-y-0.5 active:scale-95 focus:ring-2 focus:ring-emerald-900 focus:outline-none">
          <Copy className="w-4 h-4" />
        </button>
        <a href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer" data-testid={`${row.key}-wa`} title="Share on WhatsApp"
          className="w-10 h-10 grid place-items-center rounded-full bg-[#25D366] text-white transition-all duration-300 ease-out hover:brightness-105 hover:-translate-y-0.5 active:scale-95 focus:ring-2 focus:ring-emerald-900 focus:outline-none">
          <MessageCircle className="w-4 h-4" />
        </a>
        <a href={url} target="_blank" rel="noreferrer" data-testid={`${row.key}-preview`} title="Preview page"
          className="w-10 h-10 grid place-items-center rounded-full border border-stone-300 text-stone-500 transition-all duration-300 ease-out hover:border-emerald-900/40 hover:text-emerald-900 hover:-translate-y-0.5 active:scale-95 focus:ring-2 focus:ring-emerald-900 focus:outline-none">
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

export const ShareHub = ({ data }) => {
  const refLink = buildReferralLink(data.referral_code, "dashboard", "main");
  const landingUrl = `${window.location.origin}/r/${data.referral_code}`;
  const storefrontUrl = `${window.location.origin}/c/${data.referral_code}`;
  const dcaUrl = `${window.location.origin}/protect?ref=${data.referral_code}`;
  const urls = { "referrals-recruit-row": landingUrl, "referrals-storefront-row": storefrontUrl, "referrals-dca-row": dcaUrl };
  const copyMsgs = {
    "referrals-recruit-row": `👋 I'm a certified financial consultant on Crece. Loans, insurance, claims help and a free AI policy checkup — see my page:\n${landingUrl}`,
    "referrals-storefront-row": `🛍️ Browse credit cards, loans & insurance from India's top banks on my Crece storefront — plus a FREE AI policy checkup in your own language:\n${storefrontUrl}`,
    "referrals-dca-row": `🛡️ Protect your phone with Crece Device Care — screen, battery & accidental damage covered, doorstep repair at 1000+ centres. Instant quote:\n${dcaUrl}`,
  };
  const waMsgs = {
    "referrals-recruit-row": `Hey! I'd love to invite you to Crece — a free career plan for financial consultants. You'll earn weekly by sharing credit cards, loans & insurance from top banks.\n\nCheck out my page → ${landingUrl}`,
    "referrals-storefront-row": `Hi! I'm your financial advisor on Crece. Browse credit cards, loans and insurance from top banks here — happy to help you pick the right one.\n\n${storefrontUrl}`,
    "referrals-dca-row": `Protect your phone with Crece Device Care Assurance 🛡️\n\n• Covers screen, battery, motherboard & accidental damage\n• Pickup & drop repair at 1000+ centres pan-India\n• Up to 3 claims per year\n\nGet an instant quote → ${dcaUrl}`,
  };

  return (
    <div className="bg-stone-50 rounded-2xl border border-stone-200 p-7 sm:p-9" data-testid="share-hub">
      <div className="flex items-center gap-2 mb-6">
        <Share2 className="w-4 h-4 text-emerald-900" />
        <span className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-800">Your Public Pages & Referral</span>
      </div>

      {/* ticket-stub identity */}
      <div className="bg-white border-2 border-dashed border-stone-300 rounded-2xl px-6 py-5 flex items-center gap-5 flex-wrap mb-6">
        <div className="w-12 h-12 rounded-xl bg-emerald-900 text-amber-400 grid place-items-center shrink-0">
          <QrCode className="w-6 h-6" />
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-stone-400">Your referral code</div>
          <div className="font-mono text-2xl sm:text-3xl font-black tracking-widest text-emerald-900" data-testid="referral-code">{data.referral_code}</div>
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <InviteButton data-testid="dashboard-invite-button" />
          <Button data-testid="copy-referral-button"
            onClick={() => { navigator.clipboard.writeText(`🚀 Join me on Crece — India's next-generation financial super app. Free signup:\n${refLink}`); toast.success("Invite link copied!"); }}
            className="rounded-full bg-amber-500 hover:bg-amber-400 text-white font-bold h-10 px-5 transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-95">
            <Copy className="w-4 h-4 mr-2" /> Copy invite
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {ROWS.map((r, i) => (
          <Row key={r.key} row={r} url={urls[r.key]} copyMsg={copyMsgs[r.key]} waMsg={waMsgs[r.key]} delay={i * 100} />
        ))}
      </div>
    </div>
  );
};

export default ShareHub;
